<template>
  <div
    class="flex-row-union"
    :style="{
      'flex-direction': isReversed ? 'row-reverse': undefined,
    }"
  >
    <SemanticButton
      v-for="variant in shownVariantList"
      :key="variant"
      v-bind="{
        ...(sharedProps || EMPTY_OBJECT),
        ...props.buttons[variant],
      }"
      :variant="variant"
      :tooltip-position="variant === rightmostVariant ? 'left' : 'right'"
    />
  </div>
</template>

<script setup lang="ts">
import {
  computed,
  watchEffect,
} from 'vue';

import {
  EMPTY_OBJECT,
} from '../../constants/mutables';

import {
  getPresentedSeverity,
} from './qmulus-button.ts';
import {
  SEMANTIC_BUTTON_VARIANT_MAP,
  TSemanticButtonVariant,
  semanticButtonVariant,
} from './semantic-button.ts';
import {
  ISemanticButtonProps,
} from './semantic-button.vue';
import SemanticButton from './semantic-button.vue';

type TActualSemanticButtonProps = Omit<ISemanticButtonProps, 'variant'>;
export type TSemanticButtonGroupProps = Partial<Record<TSemanticButtonVariant, TActualSemanticButtonProps>>;
export type TSharedSemanticButtonGroupProps = Partial<Omit<TActualSemanticButtonProps, 'onClick' | 'label'>> & {
  class?: string,
};

const props = defineProps<{
  buttons: TSemanticButtonGroupProps,
  sharedProps?: TSharedSemanticButtonGroupProps,
}>();

const isReversed = computed(() => props.sharedProps?.presentation === 'inline');

const shownVariantList = computed(() => semanticButtonVariant.filter((variant) => props.buttons[variant] !== undefined));

// The tooltip of the rightmost button opens to the left, so that it does not leave the screen.
const rightmostVariant = computed(() => {
  return isReversed.value
    ? shownVariantList.value.at(0)
    : shownVariantList.value.at(-1);
});

const primaryVariantList = computed(() => shownVariantList.value.filter((variant) => {
  const rule = SEMANTIC_BUTTON_VARIANT_MAP[variant];
  const presentation = props.buttons[variant]?.presentation ?? props.sharedProps?.presentation ?? rule.presentation;
  return getPresentedSeverity(rule.severity, presentation) === 'primary';
}));

if (import.meta.env.DEV) {
  watchEffect(() => {
    if (primaryVariantList.value.length > 1) {
      console.warn(`[SemanticButtonGroup] An action area has one primary action, but the group shows ${primaryVariantList.value.join(', ')}`);
    }
  });
}
</script>
